import React from "react";
import { useLanguage } from "../screens/Authentication/LanguageContext";
import { siteTranslations, type SiteLocale } from "./siteTranslations";

type BilingualTag = "span" | "p" | "h1" | "h2" | "h3" | "strong" | "small";

type BilingualTextProps = {
  de: string;
  as?: BilingualTag;
  className?: string;
  id?: string;
  /** Short labels and buttons use a tighter two-line presentation. */
  compact?: boolean;
};

/**
 * German is ALWAYS the source text, followed by the chosen translation.
 * Identical strings, untranslated labels and brand names aren't doubled.
 * This component is intentionally excluded from the legacy DOM translator.
 */
export const BilingualText: React.FC<BilingualTextProps> = ({
  de, as: Element = "span", className = "", id, compact = false,
}) => {
  const { language } = useLanguage();
  const locale: SiteLocale = language === "de" ? "en" : language;
  const normalized = de.replace(/\s+/g, " ").trim().normalize("NFC");
  const translated = siteTranslations[normalized]?.[locale];
  const showTranslation = Boolean(translated && translated.normalize("NFC") !== normalized);

  return (
    <Element
      id={id}
      className={`dl-bi ${compact ? "dl-bi-compact" : ""} ${className}`.trim()}
      data-site-no-translate="true"
    >
      <span className="dl-bi-de" lang="de">{de}</span>
      {showTranslation && (
        <span className="dl-bi-target" lang={locale === "zh" ? "zh-CN" : locale} dir={locale === "ar" ? "rtl" : "ltr"}>
          {translated}
        </span>
      )}
    </Element>
  );
};
